import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Editor from '@monaco-editor/react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { auth } from '../firebase';
import useGradingJob, { GRADING_TERMINAL, gradingMessage } from '../hooks/useGradingJob';
import {
  bookmarkCodingProblem,
  getCodingPattern,
  getCodingPatterns,
  getCodingProgress,
  getCodingQuestion,
  runCode,
  saveCodingDraft,
  submitCode,
} from '../api';

const LANGUAGES = [
  { value: 'python', label: 'Python 3', monaco: 'python' },
  { value: 'javascript', label: 'JavaScript', monaco: 'javascript' },
  { value: 'java', label: 'Java', monaco: 'java' },
  { value: 'cpp', label: 'C++', monaco: 'cpp' },
  { value: 'c', label: 'C', monaco: 'c' },
];

const DEFAULT_STARTER = {
  python: '# Write your solution here\n',
  javascript: '// Write your solution here\n',
  java: 'class Main {\n  public static void main(String[] args) {\n    // Write your solution here\n  }\n}\n',
  cpp: '#include <bits/stdc++.h>\nusing namespace std;\n\nint main() {\n  // Write your solution here\n  return 0;\n}\n',
  c: '#include <stdio.h>\n\nint main() {\n  // Write your solution here\n  return 0;\n}\n',
};

function problemId(problem) {
  return problem?.problemId || problem?.questionId || problem?.problem_id || problem?.id;
}

function patternId(pattern) {
  return pattern?.patternId || pattern?.pattern_id || pattern?.title;
}

function asArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function starterCode(problem, language) {
  const starter = problem?.starterCode || {};
  if (typeof starter === 'string') return starter;
  return starter[language] || starter[language === 'cpp' ? 'c++' : language] || DEFAULT_STARTER[language] || '';
}

/* ---- Icons ---- */
const IPlay = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>;
const ICheck = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M5 12l4 4 10-10"/></svg>;
const IArrow = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>;
const IChevL = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M14 6l-6 6 6 6"/></svg>;
const IBookmark = ({ filled }) => <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h12v18l-6-4-6 4z"/></svg>;
const IClock = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>;
const IBulb = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12.74V18h8v-3.26A7 7 0 0012 2z"/></svg>;
const IRefresh = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 12a9 9 0 1018 0M21 4v6h-6"/></svg>;
const IFilter = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 6h18M6 12h12M10 18h4"/></svg>;
const ILock = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>;
const ITrend = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/></svg>;
const ILayers = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l10 5-10 5L2 7l10-5zM2 12l10 5 10-5"/></svg>;

const PATTERN_ICONS = {
  arrays: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="9" width="4" height="6" rx="1"/><rect x="10" y="9" width="4" height="6" rx="1"/><rect x="17" y="9" width="4" height="6" rx="1"/></svg>,
  'sliding window': <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="7" width="18" height="10" rx="2"/><rect x="7" y="10" width="6" height="4" fill="currentColor" stroke="none" opacity=".3"/></svg>,
  'two pointer': <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M5 12h14M5 12l3-3M5 12l3 3M19 12l-3-3M19 12l-3 3"/></svg>,
  'binary search': <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 12h6m6 0h6"/><circle cx="12" cy="12" r="2.5"/></svg>,
  tree: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="4" r="2"/><circle cx="6" cy="14" r="2"/><circle cx="18" cy="14" r="2"/><path d="M12 6l-5 7M12 6l5 7"/></svg>,
  graph: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="5" cy="6" r="2"/><circle cx="19" cy="6" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="5" cy="18" r="2"/><path d="M7 6l4 5M17 6l-4 5M7 18l4-5" strokeLinecap="round"/></svg>,
  'dynamic programming': <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor" opacity=".3"/></svg>,
  heap: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"><path d="M12 3l9 7-9 11L3 10z"/></svg>,
  greedy: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z"/></svg>,
};

function getPatternIcon(title) {
  const key = (title || '').toLowerCase();
  for (const [k, icon] of Object.entries(PATTERN_ICONS)) {
    if (key.includes(k)) return icon;
  }
  return <ILayers />;
}

/* ---- Diff pill ---- */
function DiffPill({ difficulty }) {
  const d = (difficulty || '').toLowerCase();
  return <span className={`cp-diff-pill ${d}`}>{difficulty}</span>;
}

/* ---- Status icon ---- */
function StatusIcon({ status }) {
  if (status === 'SOLVED') return <span className="cp-status-icon solved" title="Solved"><ICheck /></span>;
  if (status === 'ATTEMPTED') return (
    <span className="cp-status-icon attempted" title="Attempted">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" fill="none"/><path d="M12 8v4M12 16h.01"/></svg>
    </span>
  );
  if (status === 'IN_PROGRESS') return <span className="cp-status-icon resume" title="In progress"><IPlay /></span>;
  return (
    <span className="cp-status-icon none" title="Not started">
      <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="6"/></svg>
    </span>
  );
}

/* ---- Acceptance rate bar ---- */
function AcceptanceBar({ rate }) {
  const pct = Math.min(100, Math.max(0, rate || 0));
  const cls = pct >= 60 ? 'good' : pct >= 40 ? 'warn' : 'bad';
  return (
    <div className="cp-rate-cell">
      <div className="cp-rate-bar"><span className={cls} style={{ width: `${pct}%` }} /></div>
      <span>{pct}%</span>
    </div>
  );
}

/* ---- Progress ring SVG ---- */
function ProgressRing({ pct }) {
  const r = 46;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(100, pct || 0) / 100);
  return (
    <div className="cp-ring-wrap">
      <svg width="110" height="110" viewBox="0 0 110 110">
        <defs>
          <linearGradient id="cpRingGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#667eea" />
            <stop offset="100%" stopColor="#a78bfa" />
          </linearGradient>
        </defs>
        <circle fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" cx="55" cy="55" r={r} />
        <circle
          fill="none" stroke="url(#cpRingGrad)" strokeWidth="8" strokeLinecap="round"
          cx="55" cy="55" r={r}
          strokeDasharray={circ} strokeDashoffset={offset}
          transform="rotate(-90 55 55)"
        />
      </svg>
      <div className="cp-ring-label">
        <span className="cp-ring-pct">{Math.round(pct || 0)}%</span>
        <span className="cp-ring-sub">Mastery</span>
      </div>
    </div>
  );
}

/* ---- Company chips ---- */
function CompanyChips({ companies = [] }) {
  return (
    <div className="cp-company-chips">
      {companies.slice(0, 3).map((c) => <span key={c}>{c}</span>)}
      {companies.length > 3 && <span>+{companies.length - 3}</span>}
    </div>
  );
}

/* ---- AI section metadata ---- */
const AI_SECTIONS = [
  { keys: ['summary', 'evaluation', 'correctness', 'overall'],        label: 'Evaluation',       icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,  color: 'blue'   },
  { keys: ['time_complexity', 'time complexity'],                       label: 'Time Complexity',  icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,                                                                                                                                                              color: 'purple' },
  { keys: ['space_complexity', 'space complexity'],                     label: 'Space Complexity', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>,                                                                                                                    color: 'purple' },
  { keys: ['code_quality', 'readability', 'style'],                     label: 'Code Quality',     icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z"/></svg>,                                                                                                                                    color: 'gold'   },
  { keys: ['improvements', 'suggestions', 'feedback'],                  label: 'How to Improve',   icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12.74V18h8v-3.26A7 7 0 0012 2z"/></svg>,                                                                                                                    color: 'orange' },
  { keys: ['alternative_approach', 'better_approach', 'optimal'],       label: 'Better Approach',  icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z"/></svg>,                                                                                                                                                    color: 'accent' },
  { keys: ['edge_cases', 'edge cases', 'missed_cases'],                 label: 'Edge Cases',       icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>, color: 'red'    },
];

function matchAiSections(ai) {
  if (!ai || typeof ai !== 'object') return [];
  const used = new Set();
  const matched = [];
  for (const sec of AI_SECTIONS) {
    for (const key of sec.keys) {
      const found = Object.keys(ai).find((k) => k.toLowerCase().replace(/ /g, '_') === key.replace(/ /g, '_'));
      if (found && !used.has(found)) {
        const val = ai[found];
        if (val) { matched.push({ ...sec, value: Array.isArray(val) ? val.join('\n') : String(val) }); used.add(found); }
        break;
      }
    }
  }
  // Catch any remaining keys not matched
  for (const [k, v] of Object.entries(ai)) {
    if (!used.has(k) && v) {
      matched.push({ label: k.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()), icon: null, color: 'blue', value: Array.isArray(v) ? v.join('\n') : String(v) });
    }
  }
  return matched;
}

/* ---- AI Feedback Panel (shown below IDE after submission) ---- */
function AiFeedbackPanel({ ai, submitting, hasSubmitted, result, collapsed, onToggle, onRefresh }) {
  // Only render once a submission has been attempted
  if (!hasSubmitted && !submitting) return null;

  const sections = matchAiSections(ai);
  const passed = result?.passed;
  const score  = Math.round(result?.score || 0);

  return (
    <div className="cp-ai-fb">
      {/* ── Header ── */}
      <div className="cp-ai-fb-head">
        <span className="cp-ai-fb-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/></svg>
          AI Feedback
          {!submitting && result && (
            <span className={`cp-ai-fb-badge${passed ? ' pass' : ' fail'}`}>
              {passed ? 'Accepted' : 'Review needed'}{score > 0 ? ` · ${score}%` : ''}
            </span>
          )}
          {submitting && <span className="cp-ai-fb-badge analyzing">Analyzing…</span>}
        </span>
        <div style={{ display: 'flex', gap: '5px' }}>
          {/* Refresh button — re-fetch problem to pick up async evaluation */}
          {hasSubmitted && !submitting && !ai && (
            <button className="cp-ai-fb-toggle" title="Refresh feedback" onClick={onRefresh}>
              <IRefresh />
            </button>
          )}
          {hasSubmitted && !submitting && (
            <button className="cp-ai-fb-toggle" onClick={onToggle}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <path d={collapsed ? 'M6 9l6 6 6-6' : 'M18 15l-6-6-6 6'} />
              </svg>
            </button>
          )}
        </div>
      </div>

      {!collapsed && (
        <>
          {/* ── Shimmer while submitting ── */}
          {submitting && (
            <div className="cp-ai-fb-loading">
              <div className="coding-skeleton" style={{ height: '2.5rem', borderRadius: '8px' }} />
              <div className="coding-skeleton" style={{ height: '2rem', borderRadius: '8px', width: '75%', marginTop: '0.5rem' }} />
              <div className="coding-skeleton" style={{ height: '2rem', borderRadius: '8px', width: '55%', marginTop: '0.5rem' }} />
            </div>
          )}

          {/* ── Structured feedback sections ── */}
          {!submitting && sections.length > 0 && (
            <div className="cp-ai-fb-sections">
              {sections.map((sec, i) => (
                <div key={i} className={`cp-ai-fb-section cp-ai-fb-section--${sec.color}`}>
                  <div className="cp-ai-fb-section-head">
                    {sec.icon && <span className="cp-ai-fb-section-icon">{sec.icon}</span>}
                    <span className="cp-ai-fb-section-label">{sec.label}</span>
                  </div>
                  <div className="cp-ai-fb-section-body">{sec.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── Processing / no feedback state ── */}
          {!submitting && sections.length === 0 && (
            <div className="cp-ai-fb-pending">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/></svg>
              <div>
                <div className="cp-ai-fb-pending-title">Evaluation processing…</div>
                <div className="cp-ai-fb-pending-sub">Our AI is reviewing your code. Click <strong>Refresh</strong> in a moment to load the feedback.</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---- Result panel ---- */
function ResultPanel({ result, loading }) {
  if (loading) {
    return (
      <div className="cp-result-card">
        <div className="coding-skeleton short" />
        <div className="coding-skeleton" style={{ marginTop: '0.5rem' }} />
      </div>
    );
  }
  if (!result) return <div className="cp-result-card muted">Run or submit to see output here.</div>;
  const failures = result.failed_cases_summary || [];
  return (
    <div className="cp-result-card">
      <div className="cp-result-head">
        <span className={`cp-verdict ${result.passed ? 'passed' : 'failed'}`}>
          {result.passed ? '✓ Accepted' : result.status || 'Wrong Answer'}
        </span>
        <span className="cp-result-meta">{result.passed_testcases || 0}/{result.total_testcases || 0} tests passed</span>
        <span className="cp-result-meta">Score {Math.round(result.score || 0)}%</span>
        <span className="cp-result-meta">Runtime {result.execution_time || 0}s</span>
        <span className="cp-result-meta">Memory {result.memory || 0} KB</span>
      </div>
      {result.output && <pre className="cp-result-output">{result.output}</pre>}
      {result.error && <pre className="cp-result-output error">{result.error}</pre>}
      {failures.map((item) => (
        <div className="cp-failure-row" key={item.case}>
          <strong>Case {item.case}</strong>
          {item.visible && <small>Expected: {item.expected} | Got: {item.actual}</small>}
        </div>
      ))}
    </div>
  );
}

/* ---- Sidebar ---- */
function PatternSidebar({ patterns, activePatternId, search, onSearch, collapsed, onToggleCollapse }) {
  const navigate = useNavigate();
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return patterns;
    return patterns.filter((p) => `${p.title} ${p.group}`.toLowerCase().includes(term));
  }, [patterns, search]);

  return (
    <aside className={`cp-sidebar${collapsed ? ' collapsed' : ''}`}>
      <div className="cp-sidebar-head">
        {!collapsed && <span className="cp-sidebar-label">Learning Patterns</span>}
        <button className="cp-sidebar-collapse" onClick={onToggleCollapse} title={collapsed ? 'Expand' : 'Collapse'}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <path d={collapsed ? 'M9 6l6 6-6 6' : 'M14 6l-6 6 6 6'} />
          </svg>
        </button>
      </div>
      {!collapsed && (
        <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Filter patterns…" className="cp-sidebar-search" />
      )}
      <div className="cp-pattern-list">
        {filtered.map((pattern) => {
          const id = patternId(pattern);
          const total = pattern.totalProblems || 0;
          const solved = pattern.solvedCount || 0;
          const pct = Math.round(pattern.masteryPercentage || 0);
          const isActive = activePatternId === id || activePatternId === pattern.title;
          return (
            <button
              key={id}
              type="button"
              className={`cp-pat-item${isActive ? ' active' : ''}${pattern.locked ? ' pat-locked' : ''}`}
              title={collapsed ? pattern.title : undefined}
              onClick={() => pattern.locked ? toast('Upgrade your plan to unlock this pattern.') : navigate(`/coding/pattern/${encodeURIComponent(id)}`)}
            >
              <span className={`cp-pat-icon${isActive ? ' active' : ''}${pattern.locked ? ' pat-locked' : ''}`}>
                {pattern.locked ? <ILock /> : getPatternIcon(pattern.title)}
              </span>
              {!collapsed && (
                <>
                  <span className="cp-pat-body">
                    <span className="cp-pat-name" style={pattern.locked ? { color: 'rgba(255,255,255,0.35)' } : {}}>{pattern.title}</span>
                    {!pattern.locked && <span className="cp-pat-bar"><span style={{ width: `${pct}%` }} /></span>}
                  </span>
                  <span className="cp-pat-stat" style={pattern.locked ? { opacity: 0.4 } : {}}>
                    {pattern.locked ? <ILock /> : `${solved}/${total}`}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>
    </aside>
  );
}

/* ---- Explorer (home) ---- */
function ExplorerView({ patterns, progress }) {
  const navigate = useNavigate();
  const summary = progress?.summary || {};
  return (
    <main className="cp-main">
      <div className="setup-header setup-header--product" style={{ marginBottom: '0.25rem' }}>
        <span className="setup-eyebrow">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
          </svg>
          Coding Practice
        </span>
        <h1 className="setup-title">Coding Readiness Studio</h1>
        <p className="setup-subtitle">Build coding confidence with structured DSA practice and interview-focused challenges.</p>
      </div>
      <div className="cp-stats-row">
        {[
          { val: summary.totalSolved || 0, lbl: 'Total solved' },
          { val: summary.totalSubmissions || 0, lbl: 'Submissions' },
          { val: `${summary.acceptanceRate || 0}%`, lbl: 'Acceptance' },
          { val: summary.rankingBadge || 'Builder', lbl: 'Badge' },
        ].map(({ val, lbl }) => (
          <div key={lbl} className="cp-stat-card">
            <strong>{val}</strong>
            <span>{lbl}</span>
          </div>
        ))}
      </div>
      <div>
        <h2 className="cp-section-title">DSA Patterns</h2>
        <p className="cp-section-desc">Pick a pattern, study the concept, then solve curated interview problems.</p>
        <div className="cp-pattern-grid">
          {patterns.map((pattern) => {
            const id = patternId(pattern);
            const pct = Math.round(pattern.masteryPercentage || 0);
            return (
              <button
                key={id}
                className={`cp-pattern-tile${pattern.locked ? ' locked' : ''}`}
                onClick={() => pattern.locked ? toast('Upgrade your plan to unlock this pattern.') : navigate(`/coding/pattern/${encodeURIComponent(id)}`)}
              >
                <div className="cp-pattern-tile-top-row">
                  <div className="cp-pattern-tile-icon">{getPatternIcon(pattern.title)}</div>
                  {pattern.locked && <span className="cp-lock-badge"><ILock /> {pattern.subscriptionTier || 'Standard'}</span>}
                  {!pattern.locked && pct > 0 && <span className="cp-pct-badge">{pct}%</span>}
                </div>
                <span className="cp-pattern-tile-name">{pattern.title}</span>
                <strong className="cp-pattern-tile-count">{pattern.locked ? `${pattern.totalProblems || 10} problems` : `${pattern.solvedCount || 0}/${pattern.totalProblems || 0} solved`}</strong>
                <div className="cp-pattern-tile-bar"><span style={{ width: `${pct}%` }} /></div>
                <small>{pattern.description}</small>
                {!pattern.locked && <CompanyChips companies={pattern.companies || []} />}
              </button>
            );
          })}
        </div>
      </div>
    </main>
  );
}

/* ---- Pattern detail ---- */
function PatternDetailView({ patternIdParam }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pattern, setPattern] = useState(null);
  const [problems, setProblems] = useState([]);
  const [diffFilter, setDiffFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('Any status');
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const data = await getCodingPattern(patternIdParam);
        if (!mounted) return;
        setPattern(data.pattern);
        setProblems(data.problems || []);
      } catch (err) {
        if (mounted) setError(err.message || 'Unable to load pattern.');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [patternIdParam]);

  const filteredProblems = useMemo(() => {
    return problems.filter((p) => {
      if (diffFilter !== 'All' && (p.difficulty || '').toLowerCase() !== diffFilter.toLowerCase()) return false;
      if (statusFilter === 'Solved' && p.status !== 'SOLVED') return false;
      if (statusFilter === 'Attempted' && p.status !== 'ATTEMPTED') return false;
      if (statusFilter === 'Not started' && (p.status === 'SOLVED' || p.status === 'ATTEMPTED')) return false;
      return true;
    });
  }, [problems, diffFilter, statusFilter]);

  const startProblem = useMemo(() => {
    const open = problems.filter((p) => !p.locked);
    return open.find((p) => p.status === 'ATTEMPTED') || open.find((p) => p.status !== 'SOLVED') || open[0];
  }, [problems]);

  const pct = Math.round(pattern?.masteryPercentage || 0);
  const bd = pattern?.difficultyBreakdown || {};
  const eC = bd.easy || 0;
  const mC = bd.medium || 0;
  const hC = bd.hard || 0;
  const tot = (eC + mC + hC) || 1;

  if (loading) {
    return (
      <main className="cp-main">
        <div className="coding-skeleton short" style={{ height: '2rem', borderRadius: '8px' }} />
        <div className="coding-skeleton" style={{ height: '12rem', borderRadius: '14px' }} />
        <div className="coding-skeleton" style={{ height: '6rem', borderRadius: '10px' }} />
      </main>
    );
  }
  if (error) return <main className="cp-main"><div className="coding-error">{error}</div></main>;

  return (
    <main className="cp-main">
      {/* Breadcrumb */}
      <div className="cp-crumbs">
        <button className="cp-crumb-link" onClick={() => navigate('/coding')}>DSA Patterns</button>
        <span className="cp-crumb-sep">/</span>
        <span className="cp-crumb-current">{pattern?.title}</span>
      </div>

      {/* Pattern header */}
      <div className="cp-pattern-head">
        {/* Decorative glow — sibling element avoids overflow:hidden clipping */}
        <div className="cp-pattern-head-glow" aria-hidden="true" />

        <div className="cp-pattern-head-main">
          {/* Chips */}
          <div className="cp-pattern-chips">
            <span className="cp-chip cp-chip-ai">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/></svg>
              AI Recommended
            </span>
            <span className="cp-chip"><IClock /> ~{pattern?.estimatedHours || 6}h to mastery</span>
            <span className="cp-chip">{pattern?.totalProblems || 0} problems</span>
            <span className="cp-chip">{pattern?.group || 'Intermediate'}</span>
          </div>

          {/* Title + description */}
          <h1 className="cp-pattern-title">{pattern?.title}</h1>
          <p className="cp-pattern-desc">{pattern?.description}</p>

          {/* KPI row — bordered card */}
          <div className="cp-pattern-kpis">
            <div className="cp-kpi">
              <span className="cp-kpi-label">Progress</span>
              <span className="cp-kpi-value">{pattern?.solvedCount || 0} / {pattern?.totalProblems || 0}</span>
              <span className="cp-kpi-sub">{pct}% complete</span>
              <div className="cp-kpi-bar"><span style={{ width: `${pct}%` }} /></div>
            </div>
            <div className="cp-kpi">
              <span className="cp-kpi-label">Difficulty mix</span>
              <span className="cp-kpi-value">{eC} · {mC} · {hC}</span>
              <div className="cp-diff-strip">
                <span className="easy" style={{ flex: eC || 0 }} />
                <span className="med"  style={{ flex: mC || 0 }} />
                <span className="hard" style={{ flex: hC || 0 }} />
              </div>
              <div className="cp-diff-legend">
                <span className="easy">{eC} Easy</span>
                <span className="med">{mC} Med</span>
                <span className="hard">{hC} Hard</span>
              </div>
            </div>
            <div className="cp-kpi">
              <span className="cp-kpi-label">Est. time</span>
              <span className="cp-kpi-value">{pattern?.estimatedHours || 0}h</span>
              <span className="cp-kpi-sub">~15 min / problem</span>
            </div>
            <div className="cp-kpi">
              <span className="cp-kpi-label">Attempted</span>
              <span className="cp-kpi-value">{pattern?.attemptedCount || 0}</span>
              <span className="cp-kpi-sub">problems started</span>
            </div>
          </div>

          {/* Actions */}
          <div className="cp-pattern-actions">
            <button
              className="cp-btn cp-btn-primary"
              disabled={!startProblem}
              onClick={() => startProblem && navigate(`/coding/problem/${encodeURIComponent(problemId(startProblem))}`)}
            >
              <IArrow /> {problems.some((p) => p.status === 'ATTEMPTED') ? 'Continue learning' : 'Start learning'}
            </button>
            <button className="cp-btn">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18M3 12h18"/></svg>
              Add to plan
            </button>
            <button className="cp-btn cp-btn-ghost">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h12a4 4 0 014 4v12H8a4 4 0 01-4-4z"/></svg>
              Pattern guide
            </button>
          </div>
        </div>

        {/* Progress ring — top-right */}
        <div className="cp-pattern-head-ring">
          <ProgressRing pct={pct} />
        </div>
      </div>

      {/* Main detail grid */}
      <div className="cp-detail-grid">
        {/* Left: problem list */}
        <div>
          <div className="cp-toolbar">
            <div className="cp-seg">
              {['All', 'Easy', 'Medium', 'Hard'].map((d) => (
                <button key={d} className={diffFilter === d ? 'on' : ''} onClick={() => setDiffFilter(d)}>{d}</button>
              ))}
            </div>
            <div className="cp-seg">
              {['Any status', 'Solved', 'Attempted', 'Not started'].map((s) => (
                <button key={s} className={statusFilter === s ? 'on' : ''} onClick={() => setStatusFilter(s)}>{s}</button>
              ))}
            </div>
            <span className="cp-toolbar-spacer" />
            <span className="cp-toolbar-count">{filteredProblems.length} problems</span>
            <button className="cp-btn cp-btn-sm"><IFilter /> Sort</button>
          </div>

          <div className="cp-problem-list">
            <div className="cp-problem-list-head">
              <span />
              <span>Problem</span>
              <span>Difficulty</span>
              <span>Success</span>
              <span>Time</span>
              <span style={{ textAlign: 'center' }}>Hint</span>
              <span style={{ textAlign: 'right' }}>Action</span>
            </div>
            {filteredProblems.map((problem) => {
              const pid = problemId(problem);
              const topics = asArray(problem.topics || problem.tags || []);
              const companies = problem.companies || problem.companyTags || [];
              return (
                <div
                  key={pid}
                  className={`cp-problem-row${problem.locked ? ' locked' : ''}`}
                  onClick={() => !problem.locked && navigate(`/coding/problem/${encodeURIComponent(pid)}`)}
                >
                  <StatusIcon status={problem.status} />
                  <div className="cp-problem-title-cell">
                    <span className="cp-problem-title">{problem.title}</span>
                    <div className="cp-problem-tags">
                      {topics.slice(0, 2).map((t) => <span key={t} className="cp-mini-tag">{t}</span>)}
                      {companies.length > 0 && <span className="cp-mini-tag faang">FAANG</span>}
                    </div>
                  </div>
                  <DiffPill difficulty={problem.difficulty} />
                  <AcceptanceBar rate={problem.acceptanceRate} />
                  <span className="cp-problem-time">{problem.estimatedTime ? `${problem.estimatedTime}m` : '—'}</span>
                  <span style={{ textAlign: 'center' }}>
                    <button
                      className="cp-icon-btn"
                      title="AI hint"
                      onClick={(e) => { e.stopPropagation(); toast('AI hint coming soon!'); }}
                    >
                      <IBulb />
                    </button>
                  </span>
                  <div className="cp-row-actions" onClick={(e) => e.stopPropagation()}>
                    {problem.status === 'SOLVED' ? (
                      <>
                        <button className="cp-icon-btn" title="Review" onClick={() => navigate(`/coding/problem/${encodeURIComponent(pid)}`)}><IRefresh /></button>
                        <button className="cp-icon-btn" title="Bookmark" onClick={() => bookmarkCodingProblem({ problemId: pid, bookmarked: true }).then(() => toast.success('Bookmarked'))}><IBookmark /></button>
                      </>
                    ) : problem.status === 'ATTEMPTED' ? (
                      <button className="cp-resume-btn" onClick={() => navigate(`/coding/problem/${encodeURIComponent(pid)}`)}>
                        Resume <IArrow />
                      </button>
                    ) : (
                      <button className="cp-btn cp-btn-sm" onClick={() => navigate(`/coding/problem/${encodeURIComponent(pid)}`)}>
                        Start
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredProblems.length === 0 && (
              <div className="cp-empty-row">No problems match the selected filters.</div>
            )}
          </div>
        </div>

        {/* Right rail */}
        <div className="cp-right-rail">
          {startProblem && (
            <div className="cp-rail-card cp-continue-card">
              <div className="cp-rail-card-head">
                <span className="cp-rail-card-title"><IPlay /> Continue where you left off</span>
              </div>
              <div className="cp-continue-row">
                <div className="cp-continue-icon">{getPatternIcon(pattern?.title)}</div>
                <div className="cp-continue-info">
                  <div className="cp-continue-name">{startProblem.title}</div>
                  <div className="cp-continue-meta">{pattern?.title} · {startProblem.status === 'ATTEMPTED' ? 'In progress' : 'Not started'}</div>
                </div>
              </div>
              <button className="cp-btn cp-btn-primary" style={{ width: '100%', marginTop: '0.75rem', justifyContent: 'center' }} onClick={() => navigate(`/coding/problem/${encodeURIComponent(problemId(startProblem))}`)}>
                Resume <IArrow />
              </button>
            </div>
          )}

          <div className="cp-rail-card">
            <div className="cp-rail-card-head">
              <span className="cp-rail-card-title"><ITrend /> Your progress</span>
            </div>
            <div className="cp-mini-stats">
              <div className="cp-mini-stat"><strong>{pattern?.solvedCount || 0}</strong><span>Solved</span></div>
              <div className="cp-mini-stat"><strong>{problems.filter((p) => p.status === 'ATTEMPTED').length}</strong><span>Attempted</span></div>
              <div className="cp-mini-stat"><strong>{problems.filter((p) => !p.status || p.status === 'NOT_STARTED').length}</strong><span>Remaining</span></div>
              <div className="cp-mini-stat"><strong>{pct}%</strong><span>Mastery</span></div>
            </div>
          </div>

          <div className="cp-rail-card">
            <div className="cp-rail-card-head">
              <span className="cp-rail-card-title">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z"/></svg>
                Why this pattern?
              </span>
            </div>
            {pattern?.whyItMatters && <p className="cp-rail-body">{pattern.whyItMatters}</p>}
            {pattern?.whenToUse && (
              <>
                <div className="cp-rail-sub-title">When to use</div>
                <p className="cp-rail-body">{pattern.whenToUse}</p>
              </>
            )}
            {asArray(pattern?.commonTricks).length > 0 && (
              <>
                <div className="cp-rail-sub-title">Common tricks</div>
                <ul className="cp-rail-list">{asArray(pattern.commonTricks).map((t) => <li key={t}>{t}</li>)}</ul>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

/* ---- Timer hook ---- */
function useTimer(active) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
  return `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`;
}

/* ---- Workspace ---- */
function WorkspaceView({ problemIdParam }) {
  const navigate = useNavigate();
  const [problem, setProblem] = useState(null);
  const [nextProblem, setNextProblem] = useState(null);
  const [language, setLanguage] = useState('python');
  const [code, setCode] = useState(DEFAULT_STARTER.python);
  const [activeTab, setActiveTab] = useState('description');
  const [result, setResult] = useState(null);
  const [submitResponse, setSubmitResponse] = useState(null);
  const [gradingJobId, setGradingJobId] = useState(null);
  const grading = useGradingJob(gradingJobId);
  const gradingPending = Boolean(gradingJobId && !GRADING_TERMINAL.has(grading?.grading_status));
  const pendingAttempt = useRef(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [bookmarked, setBookmarked] = useState(false);
  const [timerActive, setTimerActive] = useState(true);
  const [mobileView, setMobileView] = useState('problem'); // 'problem' | 'code'
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [aiFbCollapsed, setAiFbCollapsed] = useState(false);
  const timer = useTimer(timerActive);
  const runRef = useRef(null);
  const submitRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const data = await getCodingQuestion(problemIdParam);
        if (!mounted) return;
        const loaded = data.question;
        const pendingKey = `vagmee-grading-${auth.currentUser?.uid}-${problemId(loaded)}`;
        try {
          pendingAttempt.current = JSON.parse(sessionStorage.getItem(pendingKey) || 'null');
          setGradingJobId(pendingAttempt.current?.jobId || null);
        } catch (_) { pendingAttempt.current = null; }
        setProblem(loaded);
        setNextProblem(data.nextRecommendedProblem || null);
        const preferred = loaded?.progress?.last_language || 'python';
        setLanguage(preferred);
        const localKey = `vagmee-coding-${problemId(loaded)}-${preferred}`;
        setCode(localStorage.getItem(localKey) || loaded?.progress?.latest_draft || starterCode(loaded, preferred));
      } catch (err) {
        if (mounted) setError(err.message || 'Unable to load problem.');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [problemIdParam]);

  useEffect(() => {
    if (grading?.grading_status !== 'completed') return;
    setSubmitResponse(grading);
    setResult(grading.result);
    setHasSubmitted(true);
    setAiFbCollapsed(false);
    if (grading.nextRecommendedProblem) setNextProblem(grading.nextRecommendedProblem);
    pendingAttempt.current = null;
    sessionStorage.removeItem(`vagmee-grading-${auth.currentUser?.uid}-${problemIdParam}`);
  }, [grading, problemIdParam]);

  useEffect(() => {
    if (!problem) return;
    const key = `vagmee-coding-${problemId(problem)}-${language}`;
    localStorage.setItem(key, code || '');
    const t = setTimeout(() => {
      if ((code || '').trim()) saveCodingDraft({ problemId: problemId(problem), language, sourceCode: code }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [code, language, problem]);

  const handleRun = useCallback(async () => {
    if (!problem || !code.trim()) { toast.error('Write code first.'); return; }
    setRunning(true);
    try {
      const data = await runCode({ problemId: problemId(problem), language, sourceCode: code });
      setResult(data.result);
    } catch (err) {
      setError(err.message || 'Run failed.');
    } finally {
      setRunning(false);
    }
  }, [code, language, problem]);

  const handleSubmit = useCallback(async () => {
    if (!problem || !code.trim()) { toast.error('Write code first.'); return; }
    if (submitting || gradingPending) return;
    setSubmitting(true);
    try {
      if (!pendingAttempt.current || pendingAttempt.current.code !== code || pendingAttempt.current.language !== language) {
        pendingAttempt.current = { idempotencyKey: crypto.randomUUID(), code, language };
      }
      const pendingKey = `vagmee-grading-${auth.currentUser?.uid}-${problemId(problem)}`;
      sessionStorage.setItem(pendingKey, JSON.stringify(pendingAttempt.current));
      const data = await submitCode({ problemId: problemId(problem), language, sourceCode: code,
        idempotencyKey: pendingAttempt.current.idempotencyKey });
      if (data.grading_job_id) {
        pendingAttempt.current.jobId = data.grading_job_id;
        sessionStorage.setItem(pendingKey, JSON.stringify(pendingAttempt.current));
        setGradingJobId(data.grading_job_id);
        setResult(null);
        toast.success('Submission saved. Grading is in progress.');
        return;
      }
      setSubmitResponse(data);
      setNextProblem(data.nextRecommendedProblem || nextProblem);
      setResult(data.result);
      setHasSubmitted(true);
      setAiFbCollapsed(false);
      toast.success(data.result?.passed ? 'Accepted!' : 'Submitted');
    } catch (err) {
      setError(err.message || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  }, [code, language, nextProblem, problem, submitting, gradingPending]);

  useEffect(() => { runRef.current = handleRun; submitRef.current = handleSubmit; }, [handleRun, handleSubmit]);

  const handleEditorMount = (editor, monaco) => {
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => runRef.current?.());
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => submitRef.current?.());
  };

  if (loading) {
    return (
      <main className="cp-main cp-workspace-outer">
        <div className="coding-skeleton" style={{ height: '4rem', borderRadius: '0', flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          <div className="coding-skeleton" style={{ borderRadius: '0' }} />
          <div className="coding-skeleton" style={{ borderRadius: '0', opacity: 0.6 }} />
        </div>
      </main>
    );
  }
  if (error && !problem) return <main className="cp-main"><div className="coding-error">{error}</div></main>;

  const examples = asArray(problem?.examples);
  const constraints = asArray(problem?.constraints);
  const hints = asArray(problem?.hints);
  const ai = submitResponse?.problemProgress?.ai_feedback || problem?.progress?.ai_feedback;

  const tabs = [
    { key: 'description', label: 'Description' },
    { key: 'examples', label: 'Examples' },
    { key: 'hints', label: hints.length ? `Hints (${hints.length})` : 'Hints' },
    { key: 'submissions', label: 'Submissions' },
  ];

  return (
    <main className="cp-main cp-workspace-outer">
      {/* Header */}
      <div className="cp-ws-head">
        <button className="cp-ws-back" onClick={() => navigate(`/coding/pattern/${encodeURIComponent(problem?.patternId || '')}`)}>
          <IChevL /> Back
        </button>
        <div className="cp-ws-title-block">
          <div className="cp-crumbs" style={{ margin: 0 }}>
            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px' }}>{problem?.patternId}</span>
            <span className="cp-crumb-sep">/</span>
            <span className="cp-crumb-current" style={{ fontSize: '12px' }}>{problem?.title}</span>
          </div>
          <div className="cp-ws-meta">
            <DiffPill difficulty={problem?.difficulty} />
            {asArray(problem?.topics || problem?.tags).slice(0, 2).map((t) => <span key={t} className="cp-chip" style={{ fontSize: '11px', padding: '2px 7px' }}>{t}</span>)}
            {ai && <span className="cp-chip cp-chip-ai" style={{ fontSize: '11px', padding: '2px 7px' }}>AI ready</span>}
          </div>
        </div>
        <div className="cp-ws-actions">
          <div className="cp-timer cp-ws-hide-mobile" onClick={() => setTimerActive((a) => !a)} title="Click to pause/resume">
            <IClock /> {timer}
          </div>
          <button
            className={`cp-icon-btn cp-ws-hide-mobile${bookmarked ? ' on' : ''}`}
            title="Bookmark"
            onClick={() => {
              setBookmarked((b) => !b);
              bookmarkCodingProblem({ problemId: problemId(problem), bookmarked: !bookmarked }).catch(() => {});
            }}
          >
            <IBookmark filled={bookmarked} />
          </button>
          <button className="cp-btn cp-btn-sm cp-ws-hide-mobile" onClick={() => { localStorage.setItem(`vagmee-coding-${problemId(problem)}-${language}`, code || ''); toast.success('Draft saved'); }}>
            Save draft
          </button>
          <button className="cp-btn cp-btn-primary cp-btn-sm" onClick={handleSubmit} disabled={submitting || gradingPending}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12l4 4 10-10"/></svg>
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>

      {/* Mobile panel switcher — only visible on small screens */}
      <div className="cp-ws-mobile-tabs">
        <button className={mobileView === 'problem' ? 'on' : ''} onClick={() => setMobileView('problem')}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>
          Problem
        </button>
        <button className={mobileView === 'code' ? 'on' : ''} onClick={() => setMobileView('code')}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M8 6L2 12l6 6M16 6l6 6-6 6"/></svg>
          Code
        </button>
      </div>

      {/* Body */}
      <div className="cp-ws-body">
        {/* Description panel */}
        <div className={`cp-ws-panel${mobileView === 'code' ? ' cp-mobile-hidden' : ''}`}>
          <div className="cp-ws-tabs">
            {tabs.map((t) => (
              <button key={t.key} className={`cp-ws-tab${activeTab === t.key ? ' on' : ''}`} onClick={() => setActiveTab(t.key)}>
                {t.dot && <span className="cp-ws-tab-dot" />}
                {t.label}
              </button>
            ))}
          </div>
          <div className="cp-ws-content">
            {activeTab === 'description' && (
              <>
                <h2 className="cp-ws-problem-title">{problem?.title}</h2>
                <div className="cp-ws-meta" style={{ marginBottom: '1rem' }}>
                  <DiffPill difficulty={problem?.difficulty} />
                  {asArray(problem?.topics || problem?.tags).map((t) => <span key={t} className="cp-chip" style={{ fontSize: '11.5px' }}>{t}</span>)}
                </div>
                <p className="cp-ws-desc">{problem?.description || problem?.prompt}</p>
                {constraints.length > 0 && (
                  <>
                    <h3 className="cp-ws-section-head">Constraints</h3>
                    <ul className="cp-ws-list">{constraints.map((c, i) => <li key={i}>{c}</li>)}</ul>
                  </>
                )}
                <CompanyChips companies={problem?.companies || problem?.companyTags || []} />
              </>
            )}

            {activeTab === 'examples' && (
              <>
                <h3 className="cp-ws-section-head">Examples</h3>
                {examples.length === 0 && <p className="cp-ws-muted">No examples provided.</p>}
                {examples.map((ex, i) => (
                  <div key={i} className="cp-example-block">
                    <div className="cp-example-label">Example {i + 1}</div>
                    {typeof ex === 'string'
                      ? <pre className="cp-example-code">{ex}</pre>
                      : <>
                          <div className="cp-example-line"><span className="cp-example-key">Input:</span> <code>{ex.input || ex.stdin || ''}</code></div>
                          <div className="cp-example-line"><span className="cp-example-key">Output:</span> <code>{ex.output || ex.expected_output || ''}</code></div>
                          {ex.explanation && <div className="cp-example-exp">{ex.explanation}</div>}
                        </>
                    }
                  </div>
                ))}
              </>
            )}

            {activeTab === 'hints' && (
              <>
                <h3 className="cp-ws-section-head">Hints</h3>
                {hints.length === 0 && <p className="cp-ws-muted">No hints available.</p>}
                {hints.map((hint, i) => (
                  <div key={i} className="cp-hint-card">
                    <div className="cp-hint-head"><IBulb /> Hint {i + 1}</div>
                    <div className="cp-hint-body">{hint}</div>
                  </div>
                ))}
              </>
            )}

            {activeTab === 'submissions' && (
              <>
                <h3 className="cp-ws-section-head">Your submissions</h3>
                <div className="cp-submission-stats">
                  <div><strong>{problem?.progress?.best_score || 0}%</strong><span>Best score</span></div>
                  <div><strong>{problem?.progress?.attempts || 0}</strong><span>Attempts</span></div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Editor panel */}
        <div className={`cp-ws-panel cp-editor-side${mobileView === 'problem' ? ' cp-mobile-hidden' : ''}`}>
          <div className="cp-editor-toolbar">
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="cp-lang-select">
              {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
            <button className="cp-icon-btn" title="Reset to starter" onClick={() => setCode(starterCode(problem, language))}><IRefresh /></button>
            <span style={{ flex: 1 }} />
            <button className="cp-btn cp-btn-sm" onClick={handleRun} disabled={running}>
              <IPlay /> {running ? 'Running…' : 'Run'}
            </button>
          </div>
          <div className="cp-editor-wrap">
            <Editor
              height="100%"
              language={LANGUAGES.find((l) => l.value === language)?.monaco || 'python'}
              theme="vs-dark"
              value={code}
              onChange={(v) => setCode(v || '')}
              onMount={handleEditorMount}
              options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, automaticLayout: true, tabSize: 2 }}
            />
          </div>
        </div>
      </div>

      {/* Footer: run results + AI feedback after submission */}
      <div className={`cp-ws-foot${(hasSubmitted || submitting) ? ' has-ai' : ''}`}>
        {gradingJobId && <p role="status" aria-live="polite">{gradingMessage(grading?.grading_status)}</p>}
        <ResultPanel result={result} loading={running || submitting} />
        <AiFeedbackPanel
          ai={ai}
          submitting={submitting}
          hasSubmitted={hasSubmitted}
          result={result}
          collapsed={aiFbCollapsed}
          onToggle={() => setAiFbCollapsed((c) => !c)}
          onRefresh={async () => {
            try {
              const data = await getCodingQuestion(problemIdParam);
              if (data?.question) setProblem(data.question);
            } catch (_) {}
          }}
        />
      </div>
    </main>
  );
}

/* ---- Root ---- */
export default function CodingPractice() {
  const { patternId: activePattern, problemId: activeProblem } = useParams();
  const [patterns, setPatterns] = useState([]);
  const [progress, setProgress] = useState(null);
  const [patternSearch, setPatternSearch] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [pr, pg] = await Promise.all([getCodingPatterns(), getCodingProgress()]);
        if (!mounted) return;
        setPatterns(pr.patterns || []);
        setProgress(pg || null);
      } catch (err) {
        if (mounted) setError(err.message || 'Unable to load.');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="cp-page">
      <div className={`cp-shell${sidebarCollapsed ? ' collapsed' : ''}`}>
        <PatternSidebar
          patterns={patterns}
          activePatternId={activePattern}
          search={patternSearch}
          onSearch={setPatternSearch}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
        />
        <div className="cp-content">
          {loading && (
            <main className="cp-main">
              <div className="coding-skeleton short" style={{ height: '2rem', borderRadius: '8px' }} />
              <div className="coding-skeleton" style={{ height: '10rem', borderRadius: '14px' }} />
            </main>
          )}
          {!loading && error && <main className="cp-main"><div className="coding-error">{error}</div></main>}
          {!loading && !error && activeProblem && <WorkspaceView problemIdParam={activeProblem} />}
          {!loading && !error && !activeProblem && activePattern && <PatternDetailView patternIdParam={activePattern} />}
          {!loading && !error && !activeProblem && !activePattern && <ExplorerView patterns={patterns} progress={progress} />}
        </div>
      </div>
    </div>
  );
}
